import { ff } from "./feature-flags";
import { createBrowserClient } from "@cieda/db";

type StockCallback = (productId: string, newStock: number) => void;
type OrderStatusCallback = (orderId: string, newStatus: string) => void;

let stockChannel: ReturnType<ReturnType<typeof createBrowserClient>["channel"]> | null = null;
let orderChannel: ReturnType<ReturnType<typeof createBrowserClient>["channel"]> | null = null;

const stockListeners = new Set<StockCallback>();
const orderListeners = new Set<OrderStatusCallback>();

/**
 * Subscribe to real-time product stock changes.
 * Shows live stock counts on product cards (like Amazon).
 */
export function subscribeToStock(callback: StockCallback): () => void {
  if (!ff.realtimeUpdates) return () => {};

  stockListeners.add(callback);

  // Create channel on first subscriber
  if (!stockChannel) {
    try {
      const supabase = createBrowserClient();
      stockChannel = supabase
        .channel("products-stock")
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "products",
            filter: "active=eq.true",
          },
          (payload) => {
            const { id, stock } = payload.new as { id: string; stock: number };
            for (const listener of stockListeners) {
              listener(id, stock);
            }
          }
        )
        .subscribe();
    } catch {
      // Realtime is non-blocking — degrade gracefully
      console.warn("[Realtime] Stock subscription failed");
    }
  }

  return () => {
    stockListeners.delete(callback);
    if (stockListeners.size === 0 && stockChannel) {
      stockChannel.unsubscribe();
      stockChannel = null;
    }
  };
}

/**
 * Subscribe to real-time order status changes.
 * Enables live tracking (like Amazon's order tracker).
 */
export function subscribeToOrderStatus(
  orderId: string,
  callback: OrderStatusCallback
): () => void {
  if (!ff.realtimeUpdates) return () => {};

  orderListeners.add(callback);

  try {
    const supabase = createBrowserClient();
    orderChannel = supabase
      .channel(`order-${orderId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "orders",
          filter: `id=eq.${orderId}`,
        },
        (payload) => {
          const { id, status } = payload.new as { id: string; status: string };
          for (const listener of orderListeners) {
            listener(id, status);
          }
        }
      )
      .subscribe();
  } catch {
    console.warn("[Realtime] Order subscription failed");
  }

  return () => {
    orderListeners.delete(callback);
    if (orderListeners.size === 0 && orderChannel) {
      orderChannel.unsubscribe();
      orderChannel = null;
    }
  };
}

/**
 * Cleanup all realtime subscriptions (on app unmount).
 */
export function cleanupRealtime(): void {
  if (stockChannel) {
    stockChannel.unsubscribe();
    stockChannel = null;
  }
  if (orderChannel) {
    orderChannel.unsubscribe();
    orderChannel = null;
  }
  stockListeners.clear();
  orderListeners.clear();
}
